import { useState } from "react";
import { createDateFromHHMM, formatDateHHMM } from "@icarius-utils/date";
import * as yup from 'yup';

const yupNumberDefinition = yup.number().transform((value, originalValue) => originalValue === "" || originalValue === null ? undefined : value);

const useHandleForm = (data, submitCallback, invalidDataCallback) => {

  const isCreate = !Boolean(data);

  const createInitialData = () => {

    const getInitialFieldData = (fieldName) => {
      if (fieldName === "length") {
        if (isCreate) return null;
        return createDateFromHHMM(data[fieldName]);
      }
      if (["useRandomOrder", "useSelfCorrection"].includes(fieldName)) return data?.[fieldName] || false;
      if (fieldName === "content") return data?.[fieldName] || null;
      if (fieldName === "resources") return data?.[fieldName] || []; // ya viene dentro de un array porque es multiple
      if (fieldName === "mediaFile") { // meterlo dentro de un array para el input
        if (data?.[fieldName]) return [data[fieldName]];
        return [];
      }
      if (fieldName === "questionsAmountToUse") return data?.[fieldName] || "";
      if (fieldName === "questions") return data?.[fieldName] || [];
      if (fieldName === "sessions") return data?.[fieldName] || [];
      if (fieldName === "removeResources") return []; // es para indicar que archivos borrar de los que vienen en el GET (solo se manda en el PUT)
      return data?.[fieldName] || "";
    }

    const fieldNames = [
      "name",
      "description",
      "module",
      "order",
      "type",
      "resources",
      "removeResources",
      "length",
      "activityDependency",
      "mediaUrl",
      "mediaId",
      "mediaFile",
      "content",
      "attempts",
      "questionsAmountToUse",
      "useRandomOrder",
      "useSelfCorrection",
      "questions",
      "sessions",
    ];

    return fieldNames.reduce((initialData, fieldName) => {
      return ({
        ...initialData,
        [fieldName]: getInitialFieldData(fieldName)
      })
    }, {});
  }

  const [formData, setFormData] = useState(() => createInitialData());

  const mediaIsValid = () => {
    const { type, mediaUrl, mediaId, mediaFile } = formData;
    if (type !== "M") return true;

    const mediaFieldsWithValueAmount = [Boolean(mediaUrl), Boolean(mediaId), Boolean(mediaFile?.length)].filter(Boolean).length;
    if (mediaFieldsWithValueAmount !== 1) return false;
    return true;
  }

  const dataIsValid = async () => {
    const schema = yup.object().shape({
      name: yup.string().required(),
      description: yup.string().required(),
      module: yup.string().required(),
      order: yup.string().required(),
      type: yup.string().required(),
      length: yup.date().required().test("hasTime", "length must not be zero", (value) => formatDateHHMM(value) !== "00:00"),
      activityDependency: yup.string(),
      resources: yup.array().nullable(),
      mediaUrl: yup.string(),
      mediaId: yup.string(),
      mediaFile: yup.array().nullable(),
      sessions: yup.array().nullable()
        .when("type", {
          is: (value) => ["S"].includes(value),
          then: yup.array().of(yup.string().required()).required()
        }),
      content: yup.object().nullable()
        .when("type", {
          is: (value) => ["L", "P", "E", "S"].includes(value),
          then: yup.object().required(),
        }),
      attempts: yupNumberDefinition
        .when("type", {
          is: (value) => ["P", "E"].includes(value),
          then: yupNumberDefinition.min(0).max(999).required(),
        }),
      questionsAmountToUse: yupNumberDefinition
        .when("type", {
          is: (value) => ["E"].includes(value),
          then: yupNumberDefinition.min(0).max(formData.questions?.length || 1).required(),
        }),
    });

    return await schema.validate(formData, { abortEarly: false })
      .then((castData) => castData)
      .catch(err => {
        if (err instanceof yup.ValidationError) {
          err.inner.forEach(error => {
            console.log(error.message);
          });
          return false;
        }
      });
  }

  const getFormDataToSend = (dataToSend) => {
    let formDataToSend = new FormData();

    const allBooleanFieldNames = [
      "useRandomOrder",
      "useSelfCorrection",
    ]

    Object.keys(dataToSend).forEach((fieldName) => {
      const fieldValue = dataToSend[fieldName];

      // array y objetos se pasan como json.stringify
      if (["content", "sessions", "questions", "removeResources"].includes(fieldName)) {
        formDataToSend.append(fieldName, JSON.stringify(fieldValue));
        return;
      }

      // array de archivos se agregan sueltos con la misma key
      if (fieldName === "resources") {
        for (const file of dataToSend[fieldName]) {
          formDataToSend.append("resources", file);
        }
        return;
      }

      // los no bool sin datos se ponen como ""
      if (!fieldValue && !allBooleanFieldNames.includes(fieldName)) {
        formDataToSend.append(fieldName, '');
        return;
      }

      formDataToSend.append(fieldName, fieldValue);
    });

    return formDataToSend;
  }

  const submit = async () => {
    if (await dataIsValid() && mediaIsValid()) {
      const dataToSend = {
        ...formData,
        length: formatDateHHMM(formData.length),
        mediaFile: formData?.mediaFile?.[0],
        resources: formData.resources.filter((resource) => !resource?.id), // no mandar los que ya vienen de back (tienen id)
      };

      // borrar campos que no sean del type seleccionado
      if (!["M"].includes(formData.type)) {
        delete dataToSend.mediaFile;
        delete dataToSend.mediaId;
        delete dataToSend.mediaUrl;
      }

      if (!["S"].includes(formData.type)) {
        delete dataToSend.sessions;
      }

      if (!["L", "P", "E", "S"].includes(formData.type)) {
        delete dataToSend.content;
      }

      if (!["P", "E"].includes(formData.type)) {
        delete dataToSend.attempts;
      }

      if (!["E"].includes(formData.type)) {
        delete dataToSend.useRandomOrder;
        delete dataToSend.useSelfCorrection;
        delete dataToSend.questionsAmountToUse;
        delete dataToSend.questions;
      }


      if (isCreate) {
        delete dataToSend.removeResources;
      }

      submitCallback(getFormDataToSend(dataToSend));
      return true;
    } else {
      invalidDataCallback();
      return false;
    }
  }

  const setSessionItem = (index, value) => {
    const auxSessions = [...formData.sessions];
    auxSessions[index] = value;

    setFormData({
      ...formData,
      sessions: auxSessions,
    });
  }

  const deleteSessionItem = (index) => {
    const auxSessions = [...formData.sessions];
    auxSessions.splice(index, 1);

    setFormData({
      ...formData,
      sessions: auxSessions,
    });
  }

  const setFormValue = (value, fieldName) => {
    if (fieldName === "removeResources") {
      // si tiene id, es un file que viene de back y se manda como eliminado en removeResources
      if (value.id) {
        // lo saco de resources
        const auxResources = [...formData.resources].filter((resource) => resource?.id !== value.id);
        // lo agrego a removeResources
        const auxRemoveResources = [...formData.removeResources];
        auxRemoveResources.push(value.id);

        setFormData({
          ...formData,
          [fieldName]: auxRemoveResources,
          resources: auxResources,
        });
        return;
      }

      // no tiene id, directamente se saca del array de resources
      const auxResources = [...formData.resources];
      const indexToDelete = auxResources.indexOf(value);
      if (indexToDelete > -1) {
        auxResources.splice(indexToDelete, 1);
        setFormData({
          ...formData,
          resources: auxResources,
        });
        return;
      }
    }

    if (fieldName === "resources") {
      setFormData({
        ...formData,
        [fieldName]: [...formData.resources, ...(value || [])],
      });
      return;
    }

    if (fieldName === "questions") {
      setFormData({
        ...formData,
        [fieldName]: value,
        useSelfCorrection: value?.some((question) => question.type === "D") ? false : formData.useSelfCorrection,
        questionsAmountToUse: value.length < formData.questionsAmountToUse ? "" : formData.questionsAmountToUse,
      });
      return;
    }

    if (fieldName === "type" && value === "S") {
      setFormData({
        ...formData,
        [fieldName]: value,
        sessions: [""],
      });
      return;
    }

    if (fieldName === "mediaUrl") {
      setFormData({
        ...formData,
        [fieldName]: value,
        mediaId: "",
        mediaFile: [],
      });
      return;
    }

    if (fieldName === "mediaId") {
      setFormData({
        ...formData,
        [fieldName]: value,
        mediaUrl: "",
        mediaFile: [],
      });
      return;
    }

    if (fieldName === "mediaFile") {
      setFormData({
        ...formData,
        [fieldName]: value,
        mediaUrl: "",
        mediaId: "",
      });
      return;
    }

    setFormData({
      ...formData,
      [fieldName]: value,
    });
  }

  return {
    isCreate,
    formData,
    setFormValue,
    setSessionItem,
    deleteSessionItem,
    submit,
  };
}

export default useHandleForm;